import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { locationsApi } from 'api/locations'
import palette from 'constants/palette'
import { HANDLING_MINUTES_PER_COMPLAINT } from 'hooks/useROICalculator'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import {
  currencyFormatter,
  numberFormatter,
  percentageFormatter,
} from 'utilities/formatters'
import { recoveryAiRoiEstimatesHelpUrl } from 'utilities/urlUtils'
import SectionShell from './SectionShell'
import SeeTheMathPanel, { MathRow } from './SeeTheMathPanel'
import useMonthlyOrders from './useMonthlyOrders'

const t = buildTranslate('recovery_ai.promo.roi')

const MINUTES_PER_HOUR = 60
const CENTS_PER_DOLLAR = 100
// Product constants behind the two ROI chains. GM_HOURLY_PAY_DOLLARS,
// RETURNING_RATE and DEFAULT_AVERAGE_CHECK_DOLLARS are each interpolated into the
// "see the math" locale row they drive, so the printed row and the derived figure
// can't drift. DEFAULT_COMPLAINT_RATE is the lone exception — see its note. The
// returning-guest rate lives here rather than in useROICalculator: that hook
// models recovered revenue with a different, conflicting formula (resolved x
// revisit x its own average order value).
/** Fallback 👎 rate, used only when the BE rate is absent. NOT printed anywhere:
 * `labor_math.complaint_rate_value` is `× %{rate}`, driven by the applied rate,
 * not this constant. Retained purely as defense-in-depth: the ROI section only
 * renders when the BE 👎 count is non-null and > 0 (plus tenure ≥ 30d, not
 * loading, orders > 0), and because that BE field ships before the FE consumes
 * it, a null rate never occurs on the live promo path — so this is unreachable
 * via the gated page. */
const DEFAULT_COMPLAINT_RATE = 0.035
/** Interpolated into `labor_math.hourly_pay_value` and `footnote_live`. */
const GM_HOURLY_PAY_DOLLARS = 30
/** Interpolated into `revenue_math.recovery_rate_value`. */
const RETURNING_RATE = 0.38
/** Interpolated into `revenue_math.check_value` — the default, used until the
 * merchant has one. */
const DEFAULT_AVERAGE_CHECK_DOLLARS = 26.57
const SECTION_PADDING = '48px 86px'
// Spearmint upside band: 28px top/bottom (trimmed 20px each from the Figma's
// 48px) to tighten its overall height, 48px left/right unchanged.
const BAND_PADDING = '28px 48px'
const CARD_PADDING = '25px'
const CARD_RADIUS = '4px'
const UPSIDE_VALUE_LINE_HEIGHT = '56px'
const METRIC_VALUE_LINE_HEIGHT = '40px'
const TREND_ICON_SIZE = 24
const DISCLOSURE_PADDING_VERTICAL = '4px'
const DISCLOSURE_PADDING_HORIZONTAL = '8px'
const DISCLOSURE_RADIUS = '4px'
/** Panel ids the disclosure buttons point at via `aria-controls`. */
const LABOR_PANEL_ID = 'roi-labor-math'
const REVENUE_PANEL_ID = 'roi-revenue-math'
// Per-stat deep-links (FR-8 v9.8, Aaron 9/16). Digital-orders and average-check
// open saved Insights Lab views; the 👎 rate opens the order-feedback report.
// dashboard.thanx.com IS this app, so these are in-app routes (React Router
// <Link>); all open in a new tab so the promo page stays put behind them.
//
// The `?s=` blobs are base64'd Insights Lab workspace state pinning report ids
// by `looker_reports.id` — a DB primary key, captured from PRODUCTION (verified
// 2026-09-18): 11 "Orders by client", 8 "Average orders per location per month",
// 12 "Orders by handoff mode"; 56 "Average check overview". Workspace.tsx drops
// any id missing from the merchant's catalog silently, so on staging/sandbox
// (ids assigned by each env's own Looker sync) or for a merchant with one of
// these reports disabled, the tab opens empty. Stable identity is
// `looker_folder_id` (643/640/351/294) if this ever needs to resolve at runtime.
export const DIGITAL_ORDERS_URL =
  '/insights_lab?s=JTdCJTIydCUyMiUzQSU1QiU3QiUyMnIlMjIlM0ExMSU3RCUyQyU3QiUyMnIlMjIlM0E4JTdEJTJDJTdCJTIyciUyMiUzQTEyJTdEJTVEJTJDJTIyYSUyMiUzQTAlN0Q'
export const AVERAGE_CHECK_URL =
  '/insights_lab?s=JTdCJTIydCUyMiUzQSU1QiU3QiUyMnIlMjIlM0E1NiU3RCU1RCUyQyUyMmElMjIlM0EwJTdE'
export const ORDER_FEEDBACK_URL = '/order_feedback'
// Figma "Spearmint-05" for the upside band — `palette.spearmintTint`, not
// `palette.spearmint05`, which holds a different (lighter) tint.
const UPSIDE_BAND_BACKGROUND = palette.spearmintTint

/**
 * Material Symbols "trending_up", inlined from the Figma export (node 129:1799).
 * The repo's FontAwesome 4 set has no trending-arrow glyph that matches, so the
 * exported path is used. Decorative — the adjacent label carries the meaning.
 */
const TrendingUpIcon = () => (
  <svg
    width={TREND_ICON_SIZE}
    height={TREND_ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3.4 18L2 16.6L9.4 9.15L13.4 13.15L18.6 8H16V6H22V12H20V9.4L13.4 16L9.4 12L3.4 18Z"
      fill={palette.spearmint50}
    />
  </svg>
)

type SeeTheMathButtonProps = {
  isOpen: boolean
  onClick: () => void
  /** Id of the breakdown panel this button expands. */
  panelId: string
}

const SeeTheMathButton = ({
  isOpen,
  onClick,
  panelId,
}: SeeTheMathButtonProps) => (
  /* uikit Button kind="minimal" forwards aria-expanded/aria-controls to the real
     <button> (borderless text-link disclosure, per CLAUDE.md). Open state picks
     up the Figma BelizeHole-10 pill fill. */
  <Button
    kind="minimal"
    aria-controls={panelId}
    aria-expanded={isOpen}
    onClick={onClick}
    $style={{
      ...(isOpen ? { backgroundColor: palette.belizeHole10 } : {}),
      borderRadius: DISCLOSURE_RADIUS,
      fontWeight: 400,
      lineHeight: '24px',
      paddingBottom: DISCLOSURE_PADDING_VERTICAL,
      paddingLeft: DISCLOSURE_PADDING_HORIZONTAL,
      paddingRight: DISCLOSURE_PADDING_HORIZONTAL,
      paddingTop: DISCLOSURE_PADDING_VERTICAL,
    }}
    endEnhancer={() => (
      <i
        className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`}
        aria-hidden="true"
      />
    )}
  >
    {isOpen ? t('hide_the_math') : t('see_the_math')}
  </Button>
)

type MetricCardProps = {
  title: string
  /** The card's resolved value, e.g. "$7,000". */
  value: string
  /** The body sentence's i18n key under `recovery_ai.promo.roi` (e.g.
   * `'labor_body'`). */
  bodyKey: string
  /** The `%{...}` interpolation key inside that body string that the
   * embedded number fills (e.g. `'hours'` for `labor_body`, `'count'` for
   * `revenue_body`). */
  bodyNumberKey: string
  /** The body sentence's own embedded number, resolved. */
  bodyNumber: string
  isOpen: boolean
  onToggle: () => void
  panelId: string
}

const MetricCard = ({
  title,
  value,
  bodyKey,
  bodyNumberKey,
  bodyNumber,
  isOpen,
  onToggle,
  panelId,
}: MetricCardProps) => {
  const [css] = useStyletron()
  const body = t(bodyKey, { [bodyNumberKey]: bodyNumber })

  return (
    <div
      className={css({
        backgroundColor: palette.white,
        // The engaged card is outlined in BelizeHole-50 while its math panel is open.
        border: `1px solid ${isOpen ? palette.belizeHole : palette.grey20}`,
        borderRadius: CARD_RADIUS,
        display: 'flex',
        flex: '1 0 0',
        flexDirection: 'column',
        gap: '24px',
        minWidth: 0,
        padding: CARD_PADDING,
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        })}
      >
        <Text.BodyText4
          tag="p"
          bold
          className={css({ lineHeight: '21px', marginBottom: 0 })}
        >
          {title}
        </Text.BodyText4>
        <div className={css({ marginBottom: 0, whiteSpace: 'nowrap' })}>
          <Text.Header2
            tag="span"
            className={css({
              alignItems: 'center',
              display: 'inline-flex',
              lineHeight: METRIC_VALUE_LINE_HEIGHT,
            })}
          >
            {value}
          </Text.Header2>
          <Text.BodyText3
            color="grey70"
            className={css({ lineHeight: '24px' })}
          >
            {t('per_month')}
          </Text.BodyText3>
        </div>
      </div>
      <Text.BodyText3
        tag="p"
        color="grey70"
        className={css({ lineHeight: '24px', marginBottom: 0 })}
      >
        {body}
      </Text.BodyText3>
      <div>
        <SeeTheMathButton
          isOpen={isOpen}
          onClick={onToggle}
          panelId={panelId}
        />
      </div>
    </div>
  )
}

/**
 * The labor chain's figures: order volume -> dissatisfied guests -> reclaimed GM
 * hours -> dollars. `hours` and `value` are the raw numbers the card's value
 * slot and body sentence show; the rest are breakdown-panel-only.
 */
type LaborFigures = {
  /** The printed "x %{rate}" row — the SAME rate actually applied to derive
   * `dissatisfiedGuests`, so the row and the count always reconcile. */
  complaintRateDisplay: string
  dissatisfiedGuests: string
  footnote: string
  headline: string
  hours: string
  orders: string
  total: string
  value: string
}

/**
 * The revenue chain's figures: order volume -> dissatisfied guests -> returning
 * guests -> dollars, at the merchant's average check. Leads with the same
 * orders/thumbs-down-rate rows as the labor chain, per Figma.
 */
type RevenueFigures = {
  /** The "× %{rate}" thumbs-down row — the SAME rate applied to derive
   * `dissatisfiedGuests`, so the row and the count reconcile (mirrors labor). */
  complaintRateDisplay: string
  /** The "× $31.02" multiplier row — measured, or the product default. */
  checkValue: string
  dissatisfiedGuests: string
  headline: string
  orders: string
  returningGuests: string
  total: string
  value: string
}

/** The band's per-location split, raw: "= %{amount} per location, across
 * your %{locations} location(s)" is built from these at render time. */
type PerLocation = {
  amount: string
  count: number
  locations: string
}

/**
 * Every figure on the section derives from the merchant's monthly orders,
 * computed together by `liveFigures()`. RoiSection only ever renders with live
 * data — index.tsx gates it (see useShowRoiSection) — so there's no loading or
 * unavailable variant of this shape.
 */
type RoiFigures = {
  labor: LaborFigures
  /**
   * `null` when there's nothing to divide the band by: the band is live but the
   * location count isn't (or can't be) known. The caller omits the whole
   * sub-line for `null` rather than fabricating a split (see `RoiSection`).
   */
  perLocation: PerLocation | null
  revenue: RevenueFigures
  /** Tagline beside the band's header: whose numbers these actually are. */
  subtitle: string
  upsideValue: string
}

/** The raw derived numbers, before formatting, shared by both chains. */
type RoiAmounts = {
  /** The rate actually applied — the live BE rate, or `DEFAULT_COMPLAINT_RATE`
   * when it isn't computable. Surfaced so the printed rate row always matches
   * the count derived from it. */
  appliedComplaintRate: number
  dissatisfiedGuests: number
  laborSavings: number
  recoveredRevenue: number
  reclaimedHours: number
  returningGuests: number
  /** Gross upside = labor savings + recovered revenue. The band shows this
   * straight sum — RecoveryAI's own cost is NOT subtracted, per Figma. */
  totalUpside: number
}

const deriveAmounts = (
  monthlyOrders: number,
  averageCheckDollars: number,
  complaintRate: number | null
): RoiAmounts => {
  // Nullish-coalesce, not ||, so a real 0 rate stays 0 rather than falling
  // back to the default (that fallback is defense-in-depth only — the gated
  // section never renders with a null rate; see DEFAULT_COMPLAINT_RATE).
  const rate = complaintRate ?? DEFAULT_COMPLAINT_RATE
  const dissatisfiedGuests = Math.round(monthlyOrders * rate)
  const handlingHours =
    (dissatisfiedGuests * HANDLING_MINUTES_PER_COMPLAINT) / MINUTES_PER_HOUR
  // FR-8: reclaimed hours round UP, and labor cost = GM pay × those (printed)
  // reclaimed hours — so the panel's hours × pay reconciles with its total.
  const reclaimedHours = Math.ceil(handlingHours)
  const laborSavings = reclaimedHours * GM_HOURLY_PAY_DOLLARS
  const returningGuests = Math.round(dissatisfiedGuests * RETURNING_RATE)
  // Multiplies the rounded (and printed) guest count so the panel's own
  // arithmetic reconciles with the total it shows.
  const recoveredRevenue = Math.round(returningGuests * averageCheckDollars)

  return {
    appliedComplaintRate: rate,
    dissatisfiedGuests,
    laborSavings,
    recoveredRevenue,
    reclaimedHours,
    returningGuests,
    totalUpside: laborSavings + recoveredRevenue,
  }
}

/**
 * numberFormatter returns the number `0` rather than a string for a zero input,
 * and these counts are interpolated into copy — normalize to a string.
 */
const formatCount = (value: number): string => `${numberFormatter(value)}`

/**
 * The band's per-location split needs a second live input (how many
 * locations the merchant runs). Without one -- no Locations access, zero
 * locations, or the locations query hasn't returned yet -- there's nothing
 * to divide by, so this reports `null` rather than fabricating a split --
 * there is no fallback figure to print here that would still be truthful
 * under a "Based on your data" subtitle. The caller omits the sub-line
 * entirely for `null` (see `RoiSection`).
 */
const resolvedPerLocation = (
  totalUpside: number,
  locationCount: number
): PerLocation | null => {
  if (locationCount <= 0) return null

  const perLocation = totalUpside / locationCount

  return {
    // Whole dollars, never cents — FR-8: "never show cents in any of the math
    // except Average check." The split is a rounded per-location figure, so it
    // won't always multiply exactly back to the band; that's accepted per spec.
    amount: currencyFormatter(perLocation, false),
    count: locationCount,
    locations: formatCount(locationCount),
  }
}

/**
 * The merchant's own average check when the endpoint reports one, else the
 * product default — the displayed multiplier row and the dollars it drives are
 * always the same figure.
 */
const averageCheck = (
  averageCheckCents: number | null
): { display: string; dollars: number } => {
  if (averageCheckCents === null) {
    return {
      display: t('revenue_math.check_value', {
        amount: currencyFormatter(DEFAULT_AVERAGE_CHECK_DOLLARS),
      }),
      dollars: DEFAULT_AVERAGE_CHECK_DOLLARS,
    }
  }

  const dollars = averageCheckCents / CENTS_PER_DOLLAR

  return {
    display: t('revenue_math.check_value_live', {
      amount: currencyFormatter(dollars),
    }),
    dollars,
  }
}

// FR-8: 👎 rate is shown to ONE decimal. The smallest such rate that still
// renders non-zero is 0.001 → "0.1%"; anything positive but below it rounds to
// the same "0%" string as a genuine zero.
const RATE_FRACTION_DIGITS = 1
const DISPLAY_FLOOR_RATE = 0.001

/**
 * The applied 👎 rate, formatted (one decimal per FR-8) for the shared rate row
 * — but floored so a positive-but-tiny rate never prints as "× 0%".
 * percentageFormatter rounds e.g. 0.02% to "0%", which would sit directly above
 * a non-zero dissatisfied-guest count and defeat the panel's whole
 * reconciliation job; render "< 0.1%" in that case. A genuine zero (rate === 0)
 * still prints a truthful "0%".
 */
const formatComplaintRate = (rate: number): string => {
  const formatted = percentageFormatter(rate, RATE_FRACTION_DIGITS)
  if (rate > 0 && formatted === percentageFormatter(0, RATE_FRACTION_DIGITS)) {
    return t('rate_below_floor', {
      rate: percentageFormatter(DISPLAY_FLOOR_RATE, RATE_FRACTION_DIGITS),
    })
  }
  return formatted
}

const liveFigures = (
  monthlyOrders: number,
  locationCount: number,
  averageCheckCents: number | null,
  complaintRate: number | null
): RoiFigures => {
  const check = averageCheck(averageCheckCents)
  const amounts = deriveAmounts(monthlyOrders, check.dollars, complaintRate)
  const hours = formatCount(amounts.reclaimedHours)
  const orders = formatCount(monthlyOrders)
  const dissatisfiedGuests = formatCount(amounts.dissatisfiedGuests)
  const returningGuests = formatCount(amounts.returningGuests)
  const laborAmount = currencyFormatter(amounts.laborSavings, false)
  const revenueAmount = currencyFormatter(amounts.recoveredRevenue, false)
  const perLocation = resolvedPerLocation(amounts.totalUpside, locationCount)
  // The thumbs-down rate row is shared by both chains — the SAME rate applied
  // to derive `dissatisfiedGuests`, so each panel's rate row reconciles with
  // the count below it. `%{rate}` is interpolated: always the live BE rate, or
  // the DEFAULT_COMPLAINT_RATE fallback — never a hardcoded 3.5%. A positive
  // rate too small to render (rounds to "0%") is floored to "< 0.01%" so it
  // never prints "× 0%" above a non-zero dissatisfied-guest count.
  const rateText = formatComplaintRate(amounts.appliedComplaintRate)

  return {
    labor: {
      complaintRateDisplay: t('labor_math.complaint_rate_value', {
        rate: rateText,
      }),
      dissatisfiedGuests,
      footnote: t('labor_math.footnote_live', { pay: GM_HOURLY_PAY_DOLLARS }),
      headline: t('labor_math.headline', { hours }),
      hours,
      orders,
      total: t('labor_math.total_value', { amount: laborAmount }),
      value: laborAmount,
    },
    perLocation,
    revenue: {
      checkValue: check.display,
      complaintRateDisplay: t('revenue_math.complaint_rate_value', {
        rate: rateText,
      }),
      dissatisfiedGuests,
      headline: t('revenue_math.headline', { count: returningGuests }),
      orders,
      returningGuests,
      total: t('revenue_math.total_value', { amount: revenueAmount }),
      value: revenueAmount,
    },
    subtitle: t('based_on'),
    upsideValue: t('upside_value_live', {
      amount: currencyFormatter(amounts.totalUpside, false),
    }),
  }
}

/** Every math-row deep-link opens in a new tab, so its accessible name carries
 * the "(opens in a new tab)" suffix (the ExternalLink glyph is the visual cue). */
const withNewTab = (label: string): string => `${label} ${t('opens_new_tab')}`

/** "View dashboard" props for an Insights Lab row — or none. `/insights_lab` is
 * a ProtectedRoute (`access` on InsightsLab) that redirects to Home, so without
 * that permission the link would open a new tab on the homepage with no
 * explanation. Omitting `href` degrades to a plain, link-free row. The
 * order-feedback row is not gated: `/order_feedback` is a plain authenticated
 * route. */
const insightsLabLink = (
  href: string,
  reportLabel: string,
  canViewInsightsLab: boolean
) =>
  canViewInsightsLab
    ? {
        href,
        linkText: t('view_dashboard'),
        linkAriaLabel: withNewTab(
          t('view_dashboard_aria', { report: reportLabel })
        ),
      }
    : {}

/** The "View source" link on a product-constant row (FR-8: every constant cites
 * the Help Center "Recovery AI ROI Estimates" article). External host ⇒
 * SeeTheMathPanel renders a plain <a>, not a Router <Link>. `metricLabel` is the
 * row's own label, so each link's accessible name stays distinct. */
const sourceLink = (metricLabel: string) => ({
  href: recoveryAiRoiEstimatesHelpUrl,
  linkText: t('view_source'),
  linkAriaLabel: withNewTab(t('view_source_aria', { metric: metricLabel })),
})

// Per Figma each factor is its OWN row: input/multiplier rows carry the grey-20
// hairline (`dividerBelow`), the derived subtotal rows are bold both sides and
// have no divider (`strongLabels`). Both chains lead with the same two rows —
// digital orders, then the shared thumbs-down rate.
const laborMathRows = (
  figures: LaborFigures,
  canViewInsightsLab: boolean
): MathRow[] => [
  {
    dividerBelow: true,
    lines: [
      {
        label: t('labor_math.orders_label'),
        value: figures.orders,
        ...insightsLabLink(
          DIGITAL_ORDERS_URL,
          t('report_purchases'),
          canViewInsightsLab
        ),
      },
    ],
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('labor_math.complaint_rate_label'),
        value: figures.complaintRateDisplay,
        href: ORDER_FEEDBACK_URL,
        linkText: t('view_dashboard'),
        linkAriaLabel: withNewTab(
          t('view_dashboard_aria', { report: t('report_feedback') })
        ),
      },
    ],
  },
  {
    lines: [
      {
        label: t('labor_math.dissatisfied_label'),
        value: figures.dissatisfiedGuests,
      },
    ],
    strongLabels: true,
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('labor_math.handling_time_label'),
        value: t('labor_math.handling_time_value', {
          minutes: HANDLING_MINUTES_PER_COMPLAINT,
        }),
        ...sourceLink(t('labor_math.handling_time_label')),
      },
    ],
  },
  {
    lines: [
      {
        label: t('labor_math.reclaimed_hours_label'),
        value: t('labor_math.reclaimed_hours_value', { hours: figures.hours }),
      },
    ],
    strongLabels: true,
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('labor_math.hourly_pay_label'),
        value: t('labor_math.hourly_pay_value', { pay: GM_HOURLY_PAY_DOLLARS }),
        ...sourceLink(t('labor_math.hourly_pay_label')),
      },
    ],
  },
]

const revenueMathRows = (
  figures: RevenueFigures,
  canViewInsightsLab: boolean
): MathRow[] => [
  {
    dividerBelow: true,
    lines: [
      {
        label: t('revenue_math.orders_label'),
        value: figures.orders,
        ...insightsLabLink(
          DIGITAL_ORDERS_URL,
          t('report_purchases'),
          canViewInsightsLab
        ),
      },
    ],
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('revenue_math.complaint_rate_label'),
        value: figures.complaintRateDisplay,
        href: ORDER_FEEDBACK_URL,
        linkText: t('view_dashboard'),
        linkAriaLabel: withNewTab(
          t('view_dashboard_aria', { report: t('report_feedback') })
        ),
      },
    ],
  },
  {
    lines: [
      {
        label: t('revenue_math.dissatisfied_label'),
        value: figures.dissatisfiedGuests,
      },
    ],
    strongLabels: true,
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('revenue_math.recovery_rate_label'),
        value: t('revenue_math.recovery_rate_value', {
          rate: percentageFormatter(RETURNING_RATE),
        }),
        ...sourceLink(t('revenue_math.recovery_rate_label')),
      },
    ],
  },
  {
    lines: [
      {
        label: t('revenue_math.returning_label'),
        value: figures.returningGuests,
      },
    ],
    strongLabels: true,
  },
  {
    dividerBelow: true,
    lines: [
      {
        label: t('revenue_math.check_label'),
        value: figures.checkValue,
        ...insightsLabLink(
          AVERAGE_CHECK_URL,
          t('report_check'),
          canViewInsightsLab
        ),
      },
    ],
  },
]

type MathPanel = 'labor' | 'revenue'

const RoiSection = () => {
  const [css] = useStyletron()
  const { averageCheckCents, complaintRate, isLoading, monthlyOrders } =
    useMonthlyOrders()

  // A loaded order count — real zero included — is what makes the band (and
  // the locations query behind its per-location split) live: a zero band
  // still divides truthfully across locations ("$0 per location").
  //
  // Also skipped without `access` on Locations: `GET /locations` is
  // `rbac_authorize!('view', 'locations')` server-side, and this promo page is
  // open to every authenticated merchant user, so an unskipped call would 403
  // and raise a danger toast (api/index.ts). A live order count alone does
  // not cover it — a viewer with `reporting` but not `locations` reaches this
  // line. Skipped ⇒ locationCount 0, which resolvedPerLocation already
  // handles.
  const ability = useAbility()
  const canViewLocations = ability.can('access', 'Locations')
  // Gates the two Insights Lab "View dashboard" rows — see insightsLabLink.
  const canViewInsightsLab = ability.can('access', 'InsightsLab')
  const { data: locationsData } = locationsApi.useGetLocationsQuery(
    { coming_soon: false },
    { skip: monthlyOrders === null || !canViewLocations }
  )
  const locationCount = locationsData?.locations.length ?? 0

  // The section's live figures. `null` only in the defensive, gate-bypassing
  // case below (no live order count); index.tsx never mounts this section
  // without one.
  const resolved = useMemo(
    (): RoiFigures | null =>
      monthlyOrders === null
        ? null
        : liveFigures(
            monthlyOrders,
            locationCount,
            averageCheckCents,
            complaintRate
          ),
    [averageCheckCents, complaintRate, locationCount, monthlyOrders]
  )

  // One open panel at most, so the two breakdowns read as an accordion: opening
  // either closes the other, and its trigger reverts to the collapsed label,
  // chevron and aria-expanded exactly as a click would leave it.
  const [openPanel, setOpenPanel] = useState<MathPanel | null>(null)

  const togglePanel = useCallback(
    (panel: MathPanel) =>
      setOpenPanel(previous => (previous === panel ? null : panel)),
    []
  )

  const toggleLabor = useCallback(() => togglePanel('labor'), [togglePanel])
  const toggleRevenue = useCallback(() => togglePanel('revenue'), [togglePanel])

  // index.tsx gates this section on live data (useShowRoiSection): it's never
  // mounted while loading or with a null order count. This guard is
  // defense-in-depth only — it keeps `resolved` non-null below and can never
  // render fabricated numbers if the component is ever mounted ungated.
  if (isLoading || monthlyOrders === null || resolved === null) return null

  return (
    <SectionShell backgroundColor={palette.white} padding={SECTION_PADDING}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        })}
      >
        <Text.Header2
          tag="h2"
          className={css({ lineHeight: '40px', marginBottom: 0 })}
        >
          {t('title')}
        </Text.Header2>

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          })}
        >
          <div
            className={css({
              alignItems: 'center',
              display: 'flex',
              height: '24px',
              justifyContent: 'space-between',
            })}
          >
            <div
              className={css({
                alignItems: 'center',
                display: 'flex',
                gap: '8px',
              })}
            >
              <TrendingUpIcon />
              <Text.Header4
                tag="p"
                className={css({
                  lineHeight: '24px',
                  marginBottom: 0,
                  whiteSpace: 'nowrap',
                })}
              >
                {t('upside_label')}
              </Text.Header4>
            </div>
            <Text.BodyText4
              tag="p"
              color="grey70"
              className={css({
                lineHeight: '20px',
                marginBottom: 0,
                whiteSpace: 'nowrap',
              })}
            >
              {resolved.subtitle}
            </Text.BodyText4>
          </div>

          <div
            className={css({
              backgroundColor: UPSIDE_BAND_BACKGROUND,
              borderRadius: CARD_RADIUS,
              padding: BAND_PADDING,
            })}
          >
            <div
              className={css({
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                whiteSpace: 'nowrap',
              })}
            >
              <div className={css({ marginBottom: 0 })}>
                <Text.Header1
                  tag="span"
                  color={palette.spearmint50}
                  className={css({
                    alignItems: 'center',
                    display: 'inline-flex',
                    lineHeight: UPSIDE_VALUE_LINE_HEIGHT,
                  })}
                >
                  {resolved.upsideValue}
                </Text.Header1>
                <Text.BodyText3
                  bold
                  color={palette.spearmint50}
                  className={css({ lineHeight: '24px' })}
                >
                  {t('per_month')}
                </Text.BodyText3>
              </div>
              {/* Omitted entirely -- not just emptied -- when the band is live
                  with no location count to split it by. With a single flex item
                  left, `justify-content: space-between` places it at the start,
                  so the value stays put and the layout doesn't jump. */}
              {resolved.perLocation && (
                <Text.Header4
                  tag="p"
                  className={css({ lineHeight: '24px', marginBottom: 0 })}
                >
                  {t('per_location_live', {
                    amount: resolved.perLocation.amount,
                    count: resolved.perLocation.count,
                    locations: resolved.perLocation.locations,
                  })}
                </Text.Header4>
              )}
            </div>
          </div>
        </div>

        <div
          className={css({
            alignItems: 'stretch',
            display: 'flex',
            gap: '16px',
          })}
        >
          <MetricCard
            title={t('revenue_title')}
            value={resolved.revenue.value}
            bodyKey="revenue_body"
            bodyNumberKey="count"
            bodyNumber={resolved.revenue.returningGuests}
            isOpen={openPanel === 'revenue'}
            onToggle={toggleRevenue}
            panelId={REVENUE_PANEL_ID}
          />
          <MetricCard
            title={t('labor_title')}
            value={resolved.labor.value}
            bodyKey="labor_body"
            bodyNumberKey="hours"
            bodyNumber={resolved.labor.hours}
            isOpen={openPanel === 'labor'}
            onToggle={toggleLabor}
            panelId={LABOR_PANEL_ID}
          />
        </div>

        {/* Per Figma the breakdown opens full-width below the card row rather
            than inside the card, so both panels can stack when both are open. */}
        {openPanel === 'labor' && (
          <SeeTheMathPanel
            id={LABOR_PANEL_ID}
            eyebrow={t('math_eyebrow', { title: t('labor_title') })}
            headline={resolved.labor.headline}
            rows={laborMathRows(resolved.labor, canViewInsightsLab)}
            total={{
              label: t('labor_math.total_label'),
              value: resolved.labor.total,
            }}
            footnote={resolved.labor.footnote}
          />
        )}
        {openPanel === 'revenue' && (
          <SeeTheMathPanel
            id={REVENUE_PANEL_ID}
            eyebrow={t('math_eyebrow', { title: t('revenue_title') })}
            headline={resolved.revenue.headline}
            rows={revenueMathRows(resolved.revenue, canViewInsightsLab)}
            total={{
              label: t('revenue_math.total_label'),
              value: resolved.revenue.total,
            }}
          />
        )}
      </div>
    </SectionShell>
  )
}

export default RoiSection
